<script setup lang="ts">
import { type AxiosResponse, isAxiosError } from "axios";
import { computed, ref } from "vue";
import { LdsToast } from "../../../../../../legl-ui/lds-toast";
import { http } from "../../../../../request-manager/axios-http";
import { PaymentSources } from "../constants/payment-sources";
import { usePayStore } from "../store";
import type { PaymentDetails } from "../types";

const payStore = usePayStore();

type ValidationErrorMessages = {
  first_name?: string;
  last_name?: string;
  email?: string;
  matter_reference?: string;
  invoice_reference?: string;
  custom_reference?: string;
  amount?: string;
};

const validationErrorMessages = ref<ValidationErrorMessages>({});

let params = new URLSearchParams(document.location.search);
let paramMatterReference = params.get("m");
let paramInvoiceReference = params.get("i");
let paramCustomReference = params.get("c");
let paramAmount = params.get("a");

// Store the original amount value to prevent currency formatting issues
const originalAmountValue = ref(paramAmount);

// Create a computed value that always returns the original amount without currency symbols
const displayAmountValue = computed(() => {
  if (!originalAmountValue.value) return null;

  // Handle URLSearchParams decoding issues with ISO-8859-1 encoded currency symbols
  // %A3 (£ in ISO-8859-1) gets converted to replacement character � (65533) in UTF-8
  let strippedAmount = originalAmountValue.value;

  // Remove replacement character and known currency symbols
  strippedAmount = Array.from(strippedAmount)
    .filter((char) => {
      const code = char.charCodeAt(0);
      // Filter out currency symbols and replacement character
      // 65533 = � (replacement character), 163 = £, 36 = $, 8364 = €
      return !(
        code === 65533 ||
        code === 163 ||
        code === 36 ||
        code === 8364 ||
        code === 164 ||
        code === 165
      );
    })
    .join("");

  // Also use regex to catch any remaining Unicode currency symbols
  strippedAmount = strippedAmount.replace(/[£$€¢¥₽₹₩₪₫₴₦₡₨₱₵₸₺₼₾₿]/g, "");

  // Validate that the stripped amount can be converted to a decimal
  const numericValue = Number.parseFloat(strippedAmount.replace(/,/g, ""));
  if (Number.isNaN(numericValue) || !Number.isFinite(numericValue)) {
    console.warn(
      `Invalid amount value after currency symbol removal: "${strippedAmount}"`,
    );
    return null;
  }

  return strippedAmount;
});

// Computed property to determine if amount field should be readonly
// Only readonly if we have a valid processed amount, not just if original param exists
const isAmountReadonly = computed(() => {
  return !!displayAmountValue.value;
});

const isSubmitting = ref(false);

async function onSubmit(event) {
  event.preventDefault();
  if (isSubmitting.value) {
    return;
  }
  try {
    isSubmitting.value = true;
    validationErrorMessages.value = {};

    const formData = new FormData(event.target);

    const amount = formData.get("amount")?.toString();

    if (amount) {
      const sanitizedAmount = amount.replaceAll(",", "");
      formData.set("amount", sanitizedAmount);
    }

    // @ts-ignore
    const postData: PaymentDetails = Object.fromEntries(formData.entries());

    if (
      payStore.checkoutSlug &&
      payStore.paymentSource === PaymentSources.CUSTOM_CHECKOUT
    ) {
      postData.checkout_slug = payStore.checkoutSlug;
    }

    let paymentDetails: PaymentDetails;

    if (payStore.paymentDetails?.reference) {
      const url = `/api/payments/${payStore.paymentDetails.reference}/`;

      const { data } = await http.put<
        PaymentDetails,
        AxiosResponse<PaymentDetails>
      >(url, postData);
      paymentDetails = data;
    } else {
      const { data } = await http.post<
        PaymentDetails,
        AxiosResponse<PaymentDetails>
      >("/api/payments/", postData);
      paymentDetails = data;
    }

    await http.put(`/api/payments/${paymentDetails.reference}/terms/`);

    payStore.setPaymentDetails(paymentDetails);
    payStore.setAvailablePaymentMethods(paymentDetails.providers);

    payStore.setCurrentStepComplete();
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 400) {
      validationErrorMessages.value = error.response.data;
    } else {
      LdsToast.showError({
        title: "An error occurred. Please try again.",
      });
    }
  } finally {
    isSubmitting.value = false;
  }
}

const showReferences = computed(() => {
  return (
    payStore.companyPaymentSettings?.matter_reference_visible ||
    payStore.companyPaymentSettings?.invoice_reference_visible ||
    payStore.companyPaymentSettings?.custom_reference_visible
  );
});
</script>

<template>
  <div data-testid="transaction-details-step">
    <h3>Transaction details</h3>
    <p>Please confirm names exactly as they appear on your government issued photo ID.</p>
    <form @submit="onSubmit">
      <div>
        <div class="grid">
          <lds-input
            name="first_name"
            label="First name"
            :errorMessage="validationErrorMessages.first_name"
            autocomplete="given-name"
          ></lds-input>
            <lds-input
            name="middle_name"
            label="Middle names"
            :optional="true"
            autocomplete="additional-name"
          ></lds-input>
          <lds-input
            name="last_name"
            label="Last name"
            :errorMessage="validationErrorMessages.last_name"
            autocomplete="family-name"
          ></lds-input>
          <lds-input
            name="email"
            label="Email"
            :errorMessage="validationErrorMessages.email"
            autocomplete="email"
          ></lds-input>
        </div>
        <div v-if="showReferences" class="grid">
          <lds-input
            v-if="payStore.companyPaymentSettings?.matter_reference_visible"
            name="matter_reference"
            :label="payStore.companyPaymentSettings?.matter_reference_label ? payStore.companyPaymentSettings?.matter_reference_label : 'Matter reference'"
            :optional="!payStore.companyPaymentSettings?.matter_reference_required"
            :help-text="payStore.companyPaymentSettings?.matter_reference_hint"
            :errorMessage="validationErrorMessages.matter_reference"
            :modelValue="paramMatterReference"
            :readonly="!!paramMatterReference"
          ></lds-input>
          <lds-input
            v-if="payStore.companyPaymentSettings?.invoice_reference_visible"
            name="invoice_reference"
            :label="payStore.companyPaymentSettings?.invoice_reference_label ? payStore.companyPaymentSettings?.invoice_reference_label : 'Invoice reference'"
            :optional="!payStore.companyPaymentSettings?.invoice_reference_required"
            :help-text="payStore.companyPaymentSettings?.invoice_reference_hint"
            :errorMessage="validationErrorMessages.invoice_reference"
            :modelValue="paramInvoiceReference"
            :readonly="!!paramInvoiceReference"
          ></lds-input>
          <lds-input
            v-if="payStore.companyPaymentSettings?.custom_reference_visible"
            name="custom_reference"
            :label="payStore.companyPaymentSettings?.custom_reference_label ? payStore.companyPaymentSettings?.custom_reference_label : 'Firm reference'"
            :optional="!payStore.companyPaymentSettings?.custom_reference_required"
            :help-text="payStore.companyPaymentSettings?.custom_reference_hint"
            :errorMessage="validationErrorMessages.custom_reference"
            :modelValue="paramCustomReference"
            :readonly="!!paramCustomReference"
          ></lds-input>
        </div>
        <div class="grid">
          <lds-input-amount
            name="amount"
            label="Amount"
            :currencyCode="payStore.companyDetails?.currency"
            :errorMessage="validationErrorMessages.amount"
            :value="displayAmountValue"
            :readonly="isAmountReadonly"
          ></lds-input-amount>
        </div>
      </div>
      <p class="terms">
        By clicking “Agree & continue” below, I agree to Legl's
        <a href="https://legl.com/terms-of-use/" target="_blank">Terms of use</a>
        &
        <a href="https://legl.com/privacy-policy/" target="_blank"
          >Privacy policy</a
        >.
      </p>
      <div class="button-container">
        <lds-button colour="primary" :disabled="isSubmitting" :isLoading="isSubmitting">Agree & continue</lds-button>
      </div>
    </form>
  </div>
</template>

<style scoped lang="scss">
h3 {
  margin: 0;
  @include typography-h3;
  color: $color-neutral-900;
}
p {
  @include typography-body-normal;
  color: $color-neutral-600;
}
.grid {
  display: grid;
  gap: $spacing-s;
  grid-template-columns: 1fr;
  align-items: end;
  border-top: 1px solid $color-neutral-200;
  padding-top: $spacing-s;
  margin-top: $spacing-m;

  &:first-of-type {
    border-top: none;
  }

  @media only screen and (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
}
.button-container {
  display: flex;
  justify-content: flex-end;
}
.terms {
  color: $color-neutral-900;
  a {
    color: $color-primary-600;
  }
}
</style>
